import { useEffect, useMemo, useRef, useState } from 'react';
import { splitGraphemes } from './graphemes';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';

type Options = {
  text: string;
  running?: boolean;
  speedMsPerChar?: number;
  startDelayMs?: number;
  showCursor?: boolean;
  cursorBlinkMs?: number;
  cursorFadeOutMs?: number;
  loop?: boolean;
};

type Result = {
  displayText: string;
  isComplete: boolean;
  cursorVisible: boolean;
  cursorOpacity: number;
  reset: () => void;
};

export function useTypewriter({
  text,
  running = true,
  speedMsPerChar = 100,
  startDelayMs = 150,
  showCursor = true,
  cursorBlinkMs = 530,
  cursorFadeOutMs = 350,
  loop = false,
}: Options): Result {
  const prefersReducedMotion = usePrefersReducedMotion();
  const glyphs = useMemo(() => splitGraphemes(text), [text]);
  const [count, setCount] = useState(0);
  const [isComplete, setIsComplete] = useState(prefersReducedMotion ? true : false);
  const [cursorVisible, setCursorVisible] = useState(true);
  const [cursorOpacity, setCursorOpacity] = useState(1);

  const typingTimer = useRef<number | null>(null);
  const blinkTimer = useRef<number | null>(null);
  const rafId = useRef<number | null>(null);

  const reset = () => {
    setCount(0);
    setIsComplete(prefersReducedMotion ? true : false);
    setCursorVisible(true);
    setCursorOpacity(1);
  };

  // Clear timers on unmount
  useEffect(() => {
    return () => {
      if (typingTimer.current) clearTimeout(typingTimer.current);
      if (blinkTimer.current) clearInterval(blinkTimer.current);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, []);

  // Typing progression
  useEffect(() => {
    if (prefersReducedMotion || !running) {
      setCount(glyphs.length);
      setIsComplete(true);
      setCursorOpacity(0);
      return;
    }

    if (glyphs.length === 0) {
      setIsComplete(true);
      setCursorOpacity(0);
      return;
    }

    // Start after delay, then type per char
    if (count < glyphs.length) {
      const delay = count === 0 ? startDelayMs : speedMsPerChar;
      typingTimer.current = window.setTimeout(() => {
        setCount((c) => Math.min(c + 1, glyphs.length));
      }, delay) as unknown as number;
    } else if (!isComplete) {
      setIsComplete(true);
    }

    return () => {
      if (typingTimer.current) clearTimeout(typingTimer.current);
    };
  }, [count, glyphs.length, prefersReducedMotion, running, isComplete, speedMsPerChar, startDelayMs]);

  // Cursor blink while typing; fade out when complete
  useEffect(() => {
    if (!showCursor || prefersReducedMotion) return;

    if (!isComplete) {
      blinkTimer.current = window.setInterval(() => {
        setCursorVisible((v) => !v);
      }, cursorBlinkMs) as unknown as number;
      return () => {
        if (blinkTimer.current) clearInterval(blinkTimer.current);
      };
    } else {
      // Fade out
      if (blinkTimer.current) clearInterval(blinkTimer.current);
      const start = performance.now();
      const animate = (t: number) => {
        const elapsed = t - start;
        const prog = Math.min(1, elapsed / cursorFadeOutMs);
        setCursorOpacity(1 - prog);
        if (prog < 1) {
          rafId.current = requestAnimationFrame(animate);
        }
      };
      rafId.current = requestAnimationFrame(animate);
      return () => {
        if (rafId.current) cancelAnimationFrame(rafId.current);
      };
    }
  }, [isComplete, showCursor, cursorBlinkMs, cursorFadeOutMs, prefersReducedMotion]);

  // Looping (optional)
  useEffect(() => {
    if (!loop || prefersReducedMotion) return;
    if (isComplete) {
      const id = window.setTimeout(() => {
        reset();
      }, 1200);
      return () => clearTimeout(id);
    }
  }, [isComplete, loop, prefersReducedMotion]);

  const displayText = useMemo(() => glyphs.slice(0, count).join(''), [glyphs, count]);
  return { displayText, isComplete, cursorVisible, cursorOpacity, reset };
}
